/**
 * Smart Network Queue for DICOM/Medical Images
 * Next-Gen: Priority Heap, Abort Listeners, Promise Deduplication, Adaptive Concurrency
 */

import { cornerstone } from '../core/init.js';
import { log } from '../utils/logger.js';
import { PriorityQueue } from '../utils/priorityQueue.js';

// [10.0.3] Куча-минимум переехала в utils/priorityQueue.js.
//
// Здесь она жила приватной копией — тот же класс слово в слово. Копия не
// мешала работать, но означала, что правка алгоритма требует помнить о втором
// экземпляре; присланный отдельный модуль это как раз и устраняет.

export class DICOMNetworkQueue {
  #queue = new PriorityQueue();
  #pendingPromises = new Map(); // imageId -> Promise (deduplication)
  #activeCount = 0;
  #concurrencyLimit;
  #metrics = {
    total: 0,
    success: 0,
    failed: 0,
    aborted: 0,
    totalRetries: 0,
    totalLoadTime: 0,
  };
  
  constructor(concurrencyLimit = 6) {
    this.#concurrencyLimit = concurrencyLimit;
    this.#detectHTTP2();
  }
  
  // [Next-Gen] Автоопределение HTTP/2 для увеличения concurrency
  #detectHTTP2() {
    // HTTP/2 позволяет мультиплексирование, можно увеличить лимит
    // В реальности проверяется через Performance API или заголовки ответа
    const isHTTP2 = performance.getEntriesByType?.('resource')
      .some(e => e.nextHopProtocol === 'h2');
    if (isHTTP2 && this.#concurrencyLimit < 10) {
      this.#concurrencyLimit = 10;
      log.info('[Network] HTTP/2 detected, increased concurrency to 10');
    }
  }

  enqueue(imageId, priority = 10, signal = null) {
    // Проверка кэша с try-catch
    try {
      if (cornerstone.cache?.getImageLoadObject?.(imageId)) {
        return Promise.resolve(imageId);
      }
    } catch (e) {
      log.warn('[Network] Cache check failed', e);
    }

    // Promise deduplication: если уже в очереди, возвращаем существующий promise
    if (this.#pendingPromises.has(imageId)) {
      return this.#pendingPromises.get(imageId);
    }

    const task = {
      imageId,
      priority,
      signal,
      retries: 0,
      maxRetries: 5,
      startTime: performance.now(),
      resolve: null,
      reject: null,
    };

    const promise = new Promise((res, rej) => {
      task.resolve = res;
      task.reject = rej;
    });

    this.#pendingPromises.set(imageId, promise);
    this.#queue.enqueue(task);
    this.#metrics.total++;

    // [CRITICAL FIX] Listener для немедленного удаления при abort
    //
    // [10.0.2] Слушатель снимается при завершении задачи (см. #detachAbort,
    // вызывается из #executeTask). Опция { once: true } снимает его только
    // ПОСЛЕ срабатывания, а для успешно загруженных кадров он не срабатывает
    // никогда — и оставался висеть на сигнале.
    //
    // Сигнал здесь один на весь набор данных, а enqueue зовётся на каждый
    // снимок: DatasetManager.load4DCine передаёт его для всех кадров всех фаз.
    // На серии 25 фаз × 200 срезов это 5000 живых замыканий на одном сигнале,
    // каждое держит свой task с resolve/reject. Ничего из этого не собирается
    // сборщиком мусора, пока набор данных не уничтожат.
    //
    // Второй эффект того же: сигнал, сработавший ПОСЛЕ успешной загрузки,
    // накручивал счётчик aborted на задачах, которые на самом деле удались.
    if (signal) {
      const abortHandler = () => {
        task.abortHandler = null;   // сам себя уже снял (once)
        this.#queue.remove(t => t.imageId === imageId);
        this.#pendingPromises.delete(imageId);
        this.#metrics.aborted++;
        task.reject(new DOMException('Aborted', 'AbortError'));
      };

      if (signal.aborted) {
        abortHandler();
        return promise;
      }

      task.abortHandler = abortHandler;
      signal.addEventListener('abort', abortHandler, { once: true });
    }

    this.#process();
    return promise;
  }

  updatePriority(imageId, newPriority) {
    const removed = this.#queue.remove(t => t.imageId === imageId);
    if (removed) {
      removed.priority = newPriority;
      this.#queue.enqueue(removed);
    }
  }

  async #process() {
    while (this.#activeCount < this.#concurrencyLimit && this.#queue.size > 0) {
      const task = this.#queue.dequeue();
      if (!task) break;
      
      this.#activeCount++;
      
      // Итеративный запуск вместо рекурсии
      this.#executeTask(task).finally(() => {
        this.#activeCount--;
        // Используем queueMicrotask для неблокирующего продолжения
        queueMicrotask(() => this.#process());
      });
    }
  }

  /** [10.0.2] Снять abort-слушатель с сигнала: задача завершилась. */
  #detachAbort(task) {
    if (task.signal && task.abortHandler) {
      try { task.signal.removeEventListener('abort', task.abortHandler); } catch { /* сигнал мёртв */ }
      task.abortHandler = null;
    }
  }

  async #executeTask(task) {
    const startTime = performance.now();

    try {
      await this.#executeWithRetry(task);
      const loadTime = performance.now() - startTime;

      this.#metrics.success++;
      this.#metrics.totalLoadTime += loadTime;
      this.#pendingPromises.delete(task.imageId);
      task.resolve(task.imageId);

    } catch (error) {
      this.#pendingPromises.delete(task.imageId);

      if (error.name === 'AbortError') {
        this.#metrics.aborted++;
        log.debug(`[Network] Aborted: ${task.imageId}`);
      } else {
        this.#metrics.failed++;
        log.error(`[Network] Failed: ${task.imageId}`, error);
      }
      task.reject(error);
    } finally {
      this.#detachAbort(task);
    }
  }

  async #executeWithRetry(task) {
    while (true) {
      if (task.signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      try {
        await cornerstone.imageLoader.loadAndCacheImage(task.imageId, { signal: task.signal });
        return;
      } catch (error) {
        if (task.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        
        task.retries++;
        this.#metrics.totalRetries++;
        
        if (task.retries > task.maxRetries) {
          throw new Error(`Max retries (${task.maxRetries}) exceeded`);
        }

        // Exponential backoff с jitter (защита от thundering herd)
        const backoffTime = 500 * Math.pow(2, task.retries - 1) + Math.random() * 200;
        log.warn(`[Network] Retry ${task.retries}/${task.maxRetries} for ${task.imageId} in ${backoffTime.toFixed(0)}ms`);
        
        await new Promise(r => setTimeout(r, backoffTime));
      }
    }
  }

  clear() {
    // Отвергаем все pending promise'ы
    for (const [imageId, promise] of this.#pendingPromises) {
      const task = this.#queue.remove(t => t.imageId === imageId);
      if (task) {
        task.reject(new DOMException('Queue cleared', 'AbortError'));
      }
    }
    this.#pendingPromises.clear();
  }

  // ── Enterprise API с метриками ──
  getState() {
    return {
      queueSize: this.#queue.size,
      activeCount: this.#activeCount,
      concurrencyLimit: this.#concurrencyLimit,
      metrics: {
        total: this.#metrics.total,
        success: this.#metrics.success,
        failed: this.#metrics.failed,
        aborted: this.#metrics.aborted,
        successRate: this.#metrics.total > 0 
          ? ((this.#metrics.success / this.#metrics.total) * 100).toFixed(1) + '%'
          : 'N/A',
        avgLoadTime: this.#metrics.success > 0
          ? (this.#metrics.totalLoadTime / this.#metrics.success).toFixed(0) + 'ms'
          : 'N/A',
        retryRate: this.#metrics.total > 0
          ? ((this.#metrics.totalRetries / this.#metrics.total) * 100).toFixed(1) + '%'
          : 'N/A',
      },
    };
  }
  
  setConcurrency(limit) {
    this.#concurrencyLimit = Math.max(1, Math.min(20, limit));
    this.#process(); // Перезапускаем с новым лимитом
  }
}

export const networkQueue = new DICOMNetworkQueue(10);

// ── Enterprise API (Backward Compatible) ──
export function getNetworkQueueState() {
  return networkQueue.getState();
}
export function clearNetworkQueue() { networkQueue.clear(); }